/**
 * CsvImportModal — "Import user data from CSV file" small modal.
 *
 * Matches the May 2026 Figma:
 *   ┌── Import user data from CSV file ──┐
 *   │  Select file  [ C://download/  📁 ] │
 *   │  Download CSV template  (link)      │
 *   │              [ Cancel ] [ Add ]     │
 *   └─────────────────────────────────────┘
 *
 * Props:
 *   open    : boolean
 *   onClose : () => void
 *   onSaved : () => void   — fired after a successful import
 */
import { useEffect, useRef, useState } from 'react';
import userService from '../../services/userService';
import { useTenant } from '../../context/TenantContext';

// CSV header expected by the backend bulk-import endpoint. Single source of
// truth so the "Download template" link and the actual upload stay in sync.
const TEMPLATE_HEADERS = [
  'firstName',
  'middleName',
  'lastName',
  'preferredName',
  'email',
  'phone',
  'customerEmployeeId',
  'externalId',
  'department',
  'departmentId',
  'timezone',
  'status',
  'startDate',
  'endDate',
];

export default function CsvImportModal({ open, onClose, onSaved }) {
  const { orgId } = useTenant();
  const [file, setFile]               = useState(null);
  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState(null);
  const [progress, setProgress]       = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setFile(null);
      setError(null);
      setProgress(null);
      setSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const onPick = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setError(null);
  };

  const downloadTemplate = () => {
    const csv = TEMPLATE_HEADERS.join(',') + '\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'davinci-user-import-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async () => {
    if (!file || submitting || !orgId) return;
    setSubmitting(true);
    setError(null);
    setProgress({ phase: 'uploading' });
    try {
      const result = await userService.bulkImportUsers(
        orgId,
        file,
        {},
        (job) => setProgress({ phase: 'processing', job }),
      );
      setProgress({ phase: 'done', result });
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.message
        ?? err?.response?.data?.error
        ?? err.message
        ?? 'Failed to import users from CSV',
      );
      setSubmitting(false);
      setProgress(null);
    }
  };

  return (
    <div
      className="os-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Import user data from CSV file"
      onMouseDown={(e) => e.target === e.currentTarget && !submitting && onClose?.()}
    >
      <div className="os-modal csv-modal" onClick={(e) => e.stopPropagation()}>
        <header className="os-modal-header">
          <h2>Import user data from CSV file</h2>
        </header>

        <div className="csv-modal-body">
          <div className="csv-row">
            <label className="csv-label">Select file</label>
            <div className="csv-file-input">
              <input
                type="text"
                readOnly
                value={file ? file.name : ''}
                placeholder="C://download/"
                onClick={() => fileRef.current?.click()}
              />
              <button
                type="button"
                className="csv-file-icon"
                onClick={() => fileRef.current?.click()}
                aria-label="Choose file"
              >
                <i className="fas fa-folder-open" />
              </button>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: 'none' }}
                onChange={onPick}
              />
            </div>
          </div>

          <button type="button" className="csv-template-link" onClick={downloadTemplate}>
            Download CSV template
          </button>

          {progress?.phase === 'processing' && (
            <div className="csv-progress">
              <i className="fas fa-spinner fa-spin" />
              {' '}Processing
              {progress.job?.total ? ` (${progress.job.processed || 0} of ${progress.job.total})` : '…'}
            </div>
          )}
          {error && <div className="os-modal-error csv-modal-error">{error}</div>}
        </div>

        <footer className="os-modal-footer">
          <button type="button" className="btn-ghost-small" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary-pill"
            onClick={handleSubmit}
            disabled={!file || submitting}
          >
            {submitting ? 'Importing…' : 'Add'}
          </button>
        </footer>
      </div>
    </div>
  );
}

